import type { AdminUiCopy } from './types';

const en: AdminUiCopy = {
  publicHeader: {
    features: 'Features',
    howItWorks: 'How it works',
    pricing: 'Pricing',
    blog: 'Blog',
    docs: 'Docs',
    signIn: 'Sign in',
    getApiKey: 'Get API Key',
  },
  login: {
    authFailed: 'Authentication failed. Please try again or use another provider.',
    sessionFailed: 'Signed in with provider, but session could not be created. Please retry.',
    notConfigured: 'SSO is not configured for this environment.',
    invalidState: 'Security check failed. Please retry the sign-in.',
    welcomeBack: 'Welcome Back',
    subtitle: 'Sign in to your dashboard',
    accessRestricted: 'Access restricted',
    accessDeniedAdminDisabled: 'Your email is not authorized. Contact support@walletgate.app if you believe this is an error.',
    accessDeniedDefault: 'Your email is not authorized.',
    contactSupport: 'Contact support',
    backToHome: 'Back to home',
    continueWithEmail: 'or continue with email',
    email: 'Email',
    emailPlaceholder: 'you@company.com',
    password: 'Password',
    passwordPlaceholder: '••••••••',
    hidePassword: 'Hide password',
    showPassword: 'Show password',
    forgotPassword: 'Forgot password?',
    signIn: 'Sign In',
    noAccount: 'No account yet?',
    createAccount: 'Create account',
  },
  register: {
    createAccount: 'Create Account',
    subtitle: 'Start with a free trial - no credit card required',
    accessRestricted: 'Access restricted',
    accessRestrictedBody: 'Registration is currently restricted. Contact support@walletgate.app if you need access.',
    contactSupport: 'Contact support',
    backToHome: 'Back to home',
    welcome: 'Welcome to WalletGate!',
    accountCreated: 'Your account has been created',
    testApiKey: 'Your Test API Key (save this now):',
    keyShownOnce: 'This key will only be shown once. You can generate more keys in your dashboard.',
    redirecting: 'Redirecting to dashboard in 5 seconds...',
    goNow: 'Go to Dashboard Now',
    continueWithEmail: 'or continue with email',
    fullName: 'Full Name',
    fullNamePlaceholder: 'John Doe',
    email: 'Email',
    emailPlaceholder: 'you@company.com',
    companyOptional: 'Company (optional)',
    companyPlaceholder: 'Acme Inc.',
    password: 'Password',
    passwordPlaceholder: '••••••••',
    hidePassword: 'Hide password',
    showPassword: 'Show password',
    passwordStrength: 'Password strength',
    tooShort: 'Too short (min 8)',
    moderate: 'Moderate',
    strong: 'Strong',
    creating: 'Creating account…',
    createButton: 'Create Account',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    registrationFailed: 'Registration failed',
    teamInviteAcceptedTitle: 'Team invite accepted',
    teamInviteAcceptedBody: "You've been added to the team. Redirecting to your team dashboard...",
    inviteLockedEmailHint: 'This email is linked to your team invite and cannot be changed.',
  },
  forgotPassword: {
    checkEmail: 'Check Your Email',
    accountExistsNoticePrefix: 'If an account exists for',
    accountExistsNoticeSuffix: 'you will receive a password reset link shortly.',
    nextSteps: 'Next steps:',
    stepInbox: 'Check your inbox for the reset email',
    stepLink: 'Click the link in the email (valid for 1 hour)',
    stepNewPassword: 'Create a new password',
    backToSignIn: 'Back to Sign In',
    title: 'Forgot Password?',
    subtitle: "Enter your email and we'll send you a reset link",
    email: 'Email',
    emailPlaceholder: 'you@company.com',
    sending: 'Sending…',
    sendResetLink: 'Send Reset Link',
    rememberPassword: 'Remember your password?',
    signIn: 'Sign in',
    sendFailed: 'Failed to send reset email',
  },
  resetPassword: {
    invalidLink: 'Invalid reset link. Please request a new password reset.',
    tooShort: 'Password must be at least 8 characters',
    mismatch: 'Passwords do not match',
    failed: 'Failed to reset password',
    successTitle: 'Password Reset',
    successBody: 'Your password has been reset successfully!',
    successCta: 'Sign In Now',
    redirecting: 'Redirecting to login in 3 seconds...',
    title: 'Reset Password',
    subtitle: 'Enter your new password',
    email: 'Email',
    emailPlaceholder: 'you@company.com',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    hidePassword: 'Hide password',
    showPassword: 'Show password',
    passwordStrength: 'Password strength',
    resetting: 'Resetting…',
    reset: 'Reset Password',
    rememberPassword: 'Remember your password?',
    signIn: 'Sign in',
  },
  notFound: {
    pageNotFound: 'Page Not Found',
    description: "The page you're looking for doesn't exist or has been moved.",
    goBack: 'Go Back',
    goDashboard: 'Go to Dashboard',
    supportPrefix: 'If you believe this is an error, please contact',
  },
  teamManagement: {
    title: 'Team',
    subtitleWithCount: '{current} of {max} members',
    subtitleDefault: 'Manage your team members',
    leaveTeam: 'Leave team',
    planLockedTitle: 'Team management is available on paid plans',
    planLockedBody: 'Upgrade to Pro to invite up to 3 team members, or Business for up to 10.',
    viewPlans: 'View plans',
    inviteMemberTitle: 'Invite a team member',
    inviteCopiedTitle: 'Invite link copied to clipboard',
    copyAgain: 'Copy',
    inviteLinkHint: 'Share this link with your team member. It expires in 7 days.',
    invitePlaceholder: 'colleague@company.com',
    roleAdmin: 'Admin',
    roleDeveloper: 'Developer',
    roleViewer: 'Viewer',
    roleAdminDescription: 'Full access',
    roleDeveloperDescription: 'Manage keys & playground',
    roleViewerDescription: 'Read-only access',
    sendInvite: 'Send invite',
    sendingInvite: 'Sending...',
    limitReachedPrefix: 'Team member limit reached.',
    upgradePlan: 'Upgrade your plan',
    membersTitle: 'Members',
    owner: 'Owner',
    ownerDescription: 'Account owner',
    joinedOn: 'Joined {date}',
    pending: 'Pending',
    remove: 'Remove',
    noMembers: 'No team members yet.',
    noMembersCta: 'Send an invite to get started.',
    pendingInvitesTitle: 'Pending invites',
    expires: 'Expires {date}',
    copyLink: 'Copy link',
    revoke: 'Revoke',
    leaveDialogTitle: 'Leave team',
    leaveDialogBody:
      "Are you sure you want to leave this team? You'll lose access to this account's resources and will need a new invite to rejoin.",
    leaveDialogConfirm: 'Leave team',
    removeDialogTitle: 'Remove team member',
    removeDialogBody: 'Remove {email} from the team? They will immediately lose access.',
    removeDialogConfirm: 'Remove',
    revokeDialogTitle: 'Revoke invite?',
    revokeDialogBody: 'This will cancel the invitation to {email}. They will not be able to join the team.',
    toastInviteCopied: 'Invite link copied to clipboard',
    toastCopiedAgain: 'Copied again',
    toastInviteRevoked: 'Invite revoked',
    toastInviteRevokeFailed: 'Failed to revoke invite',
    toastMemberRemoved: 'Team member removed',
    toastMemberRemoveFailed: 'Failed to remove team member',
    toastRoleUpdated: 'Role updated',
    toastRoleUpdateFailed: 'Failed to update role',
    toastLeftTeam: 'You have left the team',
    toastLeaveTeamFailed: 'Failed to leave team',
    inviteSendFailed: 'Failed to send invite',
  },
  auditLog: {
    title: 'Audit Log',
    subtitle: 'Track all activity on your account.',
    searchPlaceholder: 'Search by action, actor, target, IP, request ID, or event ID',
    allCategories: 'All categories',
    categorySecurity: 'Security',
    categoryApi: 'API',
    categoryDataAccess: 'Data access',
    categoryConfiguration: 'Configuration',
    allSeverities: 'All severities',
    severityInfo: 'Info',
    severityWarning: 'Warning',
    severityError: 'Error',
    severityCritical: 'Critical',
    clearFilters: 'Clear filters',
    loading: 'Loading audit logs...',
    empty: 'No audit log entries found.',
    noMatches: 'No matching entries on this page.',
    timestamp: 'Timestamp',
    eventKey: 'Event key',
    action: 'Action',
    actor: 'Actor',
    target: 'Target',
    category: 'Category',
    severity: 'Severity',
    source: 'Source',
    ipAddress: 'IP Address',
    eventId: 'Event ID',
    requestId: 'Request ID',
    userAgent: 'User agent',
    unknown: 'Unknown',
    system: 'System',
    pageSummary: 'Page {page} of {totalPages} ({total} entries)',
    previous: 'Previous',
    next: 'Next',
    tapToExpand: 'Tap to expand details',
    details: 'Details',
  },
  inviteAccept: {
    loading: 'Loading invite details...',
    noToken: 'No invite token provided.',
    invalidInviteDefault: 'This invite is invalid, expired, or has already been used.',
    acceptFailedDefault: 'Failed to accept invite. It may have expired or been revoked.',
    inviteTitle: "You've been invited",
    inviteSubtitleNeedsLogin: 'Sign in or create an account to join',
    organization: 'Organization',
    invitedBy: 'Invited by',
    yourRole: 'Your role',
    role: 'Role',
    acceptInvite: 'Accept invite',
    joiningTeam: 'Joining the team...',
    successTitle: "You're in!",
    successBodyWithOrg: "You've joined {organization}. Redirecting...",
    successBodyDefault: "You've joined the team. Redirecting...",
    errorTitle: 'Unable to accept invite',
    goLogin: 'Go to login',
    signInToAccept: 'Sign in to accept',
    createAccount: 'Create an account',
    signIn: 'Sign in',
    needsRegisterTitle: 'Create your account',
    needsRegisterBodyWithOrg:
      'Create an account with {email} to join {organization}.',
    needsRegisterBodyDefault: 'Create an account with {email} to join the team.',
    roleAdmin: 'Admin',
    roleDeveloper: 'Developer',
    roleViewer: 'Viewer',
    roleAdminDescription: 'Full access to manage keys, webhooks, billing, and team',
    roleDeveloperDescription: 'Create and manage API keys, use the playground',
    roleViewerDescription: 'Read-only access to billing and audit logs',
  },
  keys: {
    title: 'API Keys',
    createEnvLabel: 'Create key environment',
    testKey: 'Test key',
    liveKey: 'Live key',
    createKey: '+ Create Key',
    maxKeysReached: 'Maximum {env} keys reached for your plan',
    defaultHint: 'Default to Test for safe development.',
    limitReached: '{env} key limit reached.',
    revokeExisting: 'Upgrade or revoke an existing key.',
    testKeys: 'Test Keys',
    liveKeys: 'Live Keys',
    upgrade: 'Upgrade',
    newKeyShowOnce: 'New API Key (show once)',
    rotatedKeyShowOnce: 'Rotated API Key (show once)',
    prev: 'Prev',
    next: 'Next',
    pageOf: 'Page {page} of {total}',
    freePlan: 'Free Plan',
    freePlanHint: 'Upgrade to Pro (€29/mo) for 1,000 live verifications, webhooks, and more API keys.',
    viewPlans: 'View Plans →',
    filters: 'Filters',
    hideFilters: 'Hide Filters',
    showFilters: 'Show Filters',
    filter: 'Filter',
    allStatus: 'All Status',
    activeOnly: 'Active Only',
    revokedOnly: 'Revoked Only',
    environment: 'Environment',
    allEnvironments: 'All Environments',
    test: 'Test',
    live: 'Live',
    keyNameOptional: 'Key Name (optional)',
    keyNamePlaceholder: 'e.g., Production API',
    keyCountSummary: '{shown} of {total} keys',
    loading: 'Loading API keys...',
    emptyAll: 'No API keys yet.',
    emptyByStatus: 'No {status} API keys found.',
    tableName: 'Name',
    tablePublicId: 'Public ID',
    tableEnvironment: 'Environment',
    tableStatus: 'Status',
    tableCreated: 'Created',
    tableActions: 'Actions',
    unnamed: 'Unnamed',
    revoked: 'Revoked',
    active: 'Active',
    unknown: 'Unknown',
    rotate: 'Rotate',
    revoke: 'Revoke',
    rotateConfirmTitle: 'Rotate API key?',
    rotateConfirmDescription: 'This will generate a new key for "{name}" and immediately revoke the current one.',
    revokeConfirmTitle: 'Revoke API key?',
    revokeConfirmDescription: 'This will permanently disable "{name}". This action cannot be undone.',
    toastPendingKey: 'Your new test API key is ready. Copy it now; you will not see it again.',
    toastCreated: 'API key created. Copy it now; you will not see it again.',
    toastCreateFailed: 'Failed to create key',
    toastRotated: 'API key rotated. Copy it now.',
    toastRotateFailed: 'Failed to rotate key',
    toastRevoked: 'API key revoked',
    toastRevokeFailed: 'Failed to revoke key',
    toastUpgradeRequired: 'Upgrade required - View Plans in Billing',
    redirectAllowlistTitle: 'Allowed Redirect Origins',
    redirectAllowlistBody:
      'Add the origins you want WalletGate to redirect back to after verification (hosted flow). This prevents open redirects. Supports wildcard subdomains like https://*.example.com.',
    redirectAllowlistEnforceLabel: 'Enforced in production',
    redirectAllowlistPlaceholder: 'https://app.example.com or https://*.example.com',
    redirectAllowlistAdd: 'Add',
    redirectAllowlistSave: 'Save',
    redirectAllowlistSaved: 'Redirect origins saved',
    redirectAllowlistSaveFailed: 'Failed to save redirect origins',
    redirectAllowlistInvalid: 'Enter a valid https origin (or https://*.example.com)',
    redirectAllowlistDuplicate: 'This origin is already in your list',
  },
  webhooks: {
    upgradeRequired: 'Upgrade Required',
    upgradeBody: 'Webhooks are available on the Business plan.',
    viewPlans: 'View Plans',
    title: 'Webhooks',
    subtitle: 'Receive real-time events for your integrations',
    createTitle: 'Create Webhook',
    webhookUrl: 'Webhook URL (HTTPS)',
    webhookUrlPlaceholder: 'https://your-app.com/webhooks/walletgate',
    events: 'Events',
    createWebhook: 'Create Webhook',
    signingSecret: 'Webhook Signing Secret (save now)',
    existingTitle: 'Existing Webhooks',
    loading: 'Loading...',
    empty: 'No webhooks yet.',
    enabled: 'Enabled',
    disabled: 'Disabled',
    eventCount: '{count} event{suffix}',
    copyUrl: 'Copy URL',
    deleteWebhook: 'Delete',
    deleteConfirm: 'Are you sure you want to delete this webhook? This cannot be undone.',
    deleted: 'Webhook deleted.',
    deleteFailed: 'Failed to delete webhook.',
    showingRange: 'Showing {from} to {to} of {total}',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {total}',
    inspectorTitle: 'Webhook Inspector',
    inspectorLocked: 'Webhook inspector is available on Pro and Business plans.',
    loadingDeliveries: 'Loading deliveries...',
    emptyDeliveries: 'No webhook deliveries yet.',
    webhookFallbackName: 'Webhook',
    deliveryId: 'Delivery ID',
    statusLabel: 'Status',
    attemptLabel: 'Attempt',
    errorLabel: 'Error',
    payload: 'Payload',
    signature: 'Signature',
    responseBody: 'Response Body',
    retrying: 'Retrying...',
    retryDelivery: 'Retry Delivery',
    selectDelivery: 'Select a delivery to inspect.',
    clipboardUnavailable: 'Clipboard is unavailable in this browser.',
    copied: 'Webhook URL copied.',
    copyFailed: 'Failed to copy webhook URL.',
    urlMustHttps: 'URL must use HTTPS',
    urlUseServer: 'Use your server URL (not the WalletGate console)',
    urlInvalid: 'Enter a valid URL (https://...)',
    created: 'Webhook created. Save your signing secret.',
    createFailed: 'Failed to create webhook. Ensure the URL is HTTPS and reachable.',
    replayed: 'Delivery replayed.',
    replayFailed: 'Failed to retry webhook delivery.',
  },
  billing: {
    breadcrumb: 'Billing',
    staffBypass: 'Staff account: entitlements bypassed. Billing and quotas do not apply.',
    paidComingSoonTitle: 'Paid plans coming soon.',
    paidComingSoonBody: "We're finalizing our payment integration. You can continue using the Free plan in the meantime.",
    upgradeDetected: 'Upgrade to {plan} detected. You now have access to premium features like AI anomaly detection and webhooks.',
    upgradePending: 'If you completed checkout for {plan}, we’ll auto-detect your upgrade shortly. You can keep this page open.',
    refreshNow: 'Refresh now',
    goSecurity: 'Go to Security',
    title: 'Plans & Billing',
    subtitle: 'Choose the plan that fits your needs. Upgrade anytime.',
    manageBilling: 'Manage Billing',
    currentPlan: 'Current Plan',
    staff: 'Staff',
    trial: 'Trial',
    tierLabel: 'Tier: {tier}',
    internal: 'Internal',
    noBillingStaff: 'No billing for staff',
    perMonth: 'per month',
    billingCycle: 'Billing Cycle',
    nextBillDate: 'Next bill date',
    noBillingCycle: 'No billing cycle yet. Upgrade to start a subscription.',
    noBillingCycleStaff: 'Internal account. No billing cycle.',
    manageBillingArrow: 'Manage Billing →',
    free: 'Free',
    pro: 'Pro',
    business: 'Business',
    freeFeatures: [
      'Unlimited test verifications',
      '100 live verifications/month',
      '5 test keys, 2 live keys',
      'Basic sandbox (pass/fail simulation)',
      'Integration code snippets (10 languages)',
      'Email support',
    ],
    proFeatures: [
      'Unlimited test verifications',
      '1,000 live verifications/month',
      '10 test keys, 5 live keys',
      'Up to 3 team members',
      '20+ pre-built test scenarios',
      'Webhook inspector',
      '99.9% SLA',
      'Email support',
    ],
    businessFeatures: [
      'Unlimited test verifications',
      '10,000 live verifications/month',
      '10 test keys, 10 live keys',
      'Up to 10 team members',
      'Everything in Pro',
      'Build your own scenarios',
      'EUDI Readiness Report (compliance export)',
      'Country simulation (DE, FR, ES, IT, NL)',
      'Advanced analytics',
      'Priority email support',
    ],
    mostPopular: 'Most Popular',
    activePlan: 'Active Plan',
    liveVerifications: '{count} live verifications',
    currentPlanCta: 'Current Plan',
    downgrade: 'Downgrade',
    upgradeToPro: 'Upgrade to Pro',
    upgradeToBusiness: 'Upgrade to Business',
    managePlan: 'Manage Plan',
    currentOrLowerPlan: 'Current or Lower Plan',
    comingSoon: 'Coming Soon',
    enterpriseTitle: 'Need more than 10,000 verifications?',
    enterpriseBody: 'Contact us for custom pricing with volume discounts, dedicated support, and SLAs.',
    contactSales: 'Contact Sales',
    downgradeHint: 'To downgrade, use the billing portal. Changes take effect at the end of your billing period.',
    toastUpgradeSuccess: 'Upgraded to {plan}. Prorated charges will appear on your next invoice.',
    toastOpenPortalFailed: 'Failed to open billing portal',
    toastCheckoutFallback: 'Checkout not available. Opening billing portal...',
  },
  playground: {
    export: 'Export',
    quickTour: 'Quick Tour',
    stepOf: 'Step {current} of {total}',
    skip: 'Skip',
    nextTip: 'Next tip',
    done: 'Done',
    quickStart: 'Quick Start',
    quickStartSubtitle: 'Pick a use case and verify in seconds, or configure your own.',
    onboardingConfigureTitle: 'Configure checks',
    onboardingConfigureMessage: 'Pick a quick template or add checks in the Configuration panel, then click Start Verification.',
    onboardingFlowTitle: 'Watch the live flow',
    onboardingFlowMessage: 'Track each milestone as the session progresses from handoff to decision.',
    onboardingShipTitle: 'Ship faster',
    onboardingShipMessage: 'Export this run as JSON or copy the integration code snippet for your app.',
    configureNow: 'Configure now',
    tryNow: 'Try it now →',
    checksConfigured: '{count} checks configured',
    howConfidenceMeasured: 'How confidence is measured',
    sandboxToolkit: 'Sandbox Toolkit',
    scenarioName: 'Scenario Name',
    scenarioNamePlaceholder: 'e.g. Underage + EU Residency',
    description: 'Description',
    scenarioDescriptionPlaceholder: 'Optional summary for your team',
    defaultResult: 'Default Result',
    defaultPass: 'Default Pass',
    defaultFail: 'Default Fail',
    checkOverrides: 'Check Overrides',
    allPass: 'All Pass',
    allFail: 'All Fail',
    addChecksHint: 'Add checks above to configure scenario results.',
    saveScenario: 'Save Scenario',
    saving: 'Saving...',
    createScenarioFailed: 'Failed to create scenario',
    savedScenarios: 'Saved Scenarios',
    totalCount: '{count} total',
    noCustomScenarios: 'No custom scenarios yet. Create one to reuse across sessions.',
    use: 'Use',
    useScenarioTitle: 'Load checks and pre-select for simulation',
    deleteScenarioConfirm: 'Delete this scenario?',
    defaultPassLabel: 'Default pass',
    defaultFailLabel: 'Default fail',
    range: 'Range',
    runsCaptured: '{count} runs captured in this period.',
    checksPass: '{passed}/{total} checks pass',
    runsUsed: '{used} / {total} runs used',
  }, 
  shared: {
    copy: 'Copy',
    copied: 'Copied!',
    confirm: 'Confirm',
    cancel: 'Cancel',
    loading: 'Loading...',
    notAuthorized: 'Not authorized',
    accessDenied: 'Access Denied',
    accessDeniedBody: 'You do not have permission to view this page.',
    support: 'support@walletgate.app',
    refresh: 'Refresh',
    errorBoundaryTitle: 'Something Went Wrong',
    errorBoundaryDescription: "We encountered an unexpected error. Don't worry, your data is safe.",
    errorBoundaryTryAgain: 'Try Again',
    errorBoundaryGoDashboard: 'Go to Dashboard',
    errorBoundaryContact: 'If this problem persists, please contact',
  },
  publicFooter: {
    tagline: 'EUDI Wallet verification API for developers.',
    product: 'Product',
    resources: 'Resources',
    legal: 'Legal',
    features: 'Features',
    pricing: 'Pricing',
    documentation: 'Documentation',
    blog: 'Blog',
    npm: 'NPM',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    rightsReserved: 'All rights reserved.',
  },
  earlyAccessBanner: {
    program: 'Early access program',
    register: 'Register',
  },
  billingCancel: {
    title: 'Checkout Canceled',
    body: 'No changes made to your subscription.',
    backToBilling: 'Back to Billing',
    dashboard: 'Dashboard',
  },
  billingSuccess: {
    title: 'Payment Successful',
    bodyWithPlan: 'Your plan has been upgraded to {plan}.',
    bodyDefault: 'Your plan has been updated.',
    goSecurity: 'Go to Security',
    backToBilling: 'Back to Billing',
  },
  blog: {
    title: 'Blog',
    subtitle: 'Product updates, EUDI insights, and engineering deep-dives from the WalletGate team.',
    all: 'All',
    comingSoonTitle: 'Coming soon',
    comingSoonBody: "We're working on articles about EUDI wallet integration, product updates, and engineering insights. Stay tuned.",
    serviceUnavailableTitle: 'Blog is temporarily unavailable',
    serviceUnavailableBody: "We're having trouble loading articles right now. Please try again shortly.",
    readDocs: 'Read the docs',
    getStarted: 'Get started',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {total}',
    minRead: '{count} min read',
  },
  blogPost: {
    notFoundTitle: 'Post not found',
    notFoundBody: "The blog post you're looking for doesn't exist or has been removed.",
    loadErrorTitle: 'Unable to load post',
    loadErrorBody: 'Something went wrong while loading this post.',
    backToBlog: 'Back to blog',
    minRead: '{count} min read',
  },
  editorToolbar: {
    imageAltPrompt: 'Image alt text (for accessibility):',
    uploadFailed: 'Upload failed',
    linkUrlPrompt: 'Link URL:',
    youtubeUrlLabel: 'YouTube Video URL',
    youtubePlaceholder: 'https://www.youtube.com/watch?v=...',
    embed: 'Embed',
    cancel: 'Cancel',
    bold: 'Bold',
    italic: 'Italic',
    strikethrough: 'Strikethrough',
    heading2: 'Heading 2',
    heading3: 'Heading 3',
    bulletList: 'Bullet list',
    numberedList: 'Numbered list',
    blockquote: 'Blockquote',
    codeBlock: 'Code block',
    insertTable: 'Insert table',
    addLink: 'Add link',
    uploadImage: 'Upload image',
    uploading: 'Uploading...',
    embedYoutube: 'Embed YouTube video',
    undo: 'Undo',
    redo: 'Redo',
    importMarkdown: 'Import Markdown',
  },
  dashboard: {
    overview: 'Overview',
    noPriorData: 'No prior data',
    noChange: 'No change',
    vsLastPeriod: '{value}% vs last period',
    last24Hours: 'Last 24 hours',
    last30Days: 'Last 30 days',
    last7Days: 'Last 7 days',
    hour: 'hour',
    day: 'day',
    totalVerifications: 'Total Verifications',
    successRate: 'Success Rate',
    activeSessions: 'Active Sessions',
    currentlyInProgress: 'Currently in progress',
    verificationTrend: 'Verification Trend',
    trackVerificationVolume: 'Track verification volume over time',
    total: 'Total',
    completed: 'Completed',
    failed: 'Failed',
    average: 'Average',
    peak: 'Peak',
    perBucket: 'Per {bucket}',
    avgPerBucket: 'Avg per {bucket}',
    noDataYet: 'No data yet',
    verificationsWillAppear: 'Verifications will appear here once you start',
    currentPlan: 'Current Plan',
    manage: 'Manage',
    staff: 'Staff',
    internalAccount: 'Internal account',
    noBillingForStaff: 'No billing for staff',
    liveVerificationsThisMonth: 'Live Verifications (This Month)',
    upgradeForLiveAccess: 'Upgrade for live access',
    nextBillDate: 'Next Bill Date',
    status: 'Status',
    daysRemaining: '{days} days remaining',
    renewingSoon: 'Renewing soon',
    gracePeriod: 'Grace Period',
    daysToUpdatePayment: '{days} days to update payment',
    active: 'Active',
    allSystemsOperational: 'All systems operational',
    advancedAnalytics: 'Advanced Analytics',
    detailedBreakdown: 'Detailed breakdown by environment and API key',
    exportCsv: 'Export CSV',
    byEnvironment: 'By Environment',
    byStatus: 'By Status',
    live: 'Live',
    test: 'Test',
    topApiKeysThisMonth: 'Top API Keys (This Month)',
    advancedAnalyticsUpgrade: 'Advanced Analytics. Upgrade for detailed breakdowns and CSV export.',
    upgrade: 'Upgrade',
    developerResources: 'Developer Resources',
    documentation: 'Documentation',
    typescriptSdk: 'TypeScript SDK',
    onboardingWelcome: 'Welcome to WalletGate',
    youreAllSet: "You're all set!",
    accountReady: 'Your account is ready. Complete your first verification in under 5 minutes.',
    getStartedIn3Steps: 'Get started in 3 steps',
    getYourApiKey: 'Get your API key',
    apiKeyReady: 'You already have an API key ready to use',
    createFirstApiKey: 'Create your first API key to authenticate requests',
    viewApiKeys: 'View API Keys',
    createApiKey: 'Create API Key',
    installSdk: 'Install the SDK',
    addWalletGate: 'Add WalletGate to your project with a single command',
    copyCommand: 'Copy command',
    copied: 'Copied!',
    makeFirstRequest: 'Make your first request',
    startVerifying: 'Start verifying digital identities with a few lines of code',
    viewDocumentation: 'View Documentation',
    quickStartExample: 'Quick Start Example',
    copyCode: 'Copy code',
    apiDocumentation: 'API Documentation',
    apiDocumentationDesc: 'Complete API reference and guides',
    codeExamples: 'Code Examples',
    codeExamplesDesc: 'Sample integrations in multiple languages',
    support: 'Support',
    supportDesc: 'Get help from our team',
    community: 'Community',
    communityDesc: 'Join other developers',
    complete: 'Complete',
    noPriorRange: 'No prior {range}',
    noChangeVsPrior: 'No change vs prior {range}',
    vsPriorRange: '{value}% vs prior {range}',
  },
  security: {
    title: 'Security Center',
    breadcrumb: 'Security',
    threatMonitoringActive: 'Threat monitoring active',
    refreshData: 'Refresh data',
    updated: 'Updated',
    realtimeDescription: 'Real-time anomaly detection, fraud prevention, and clear security insights',
    activeAlerts: 'Active Alerts',
    criticalThreats: 'Critical Threats',
    requestsToday: 'Requests Today',
    uniqueIps: 'Unique IPs',
    total: 'total',
    highPriority: 'High priority',
    failureRate: 'failure rate',
    geographicSpread: 'Geographic spread',
    searchPlaceholder: 'Search alerts by type, IP, or description...',
    all: 'All',
    active: 'Active',
    resolved: 'Resolved',
    securityAlerts: 'Security Alerts',
    detectedAnomalies: 'Detected anomalies and threats',
    noMatchingAlerts: 'No matching alerts',
    adjustFilters: 'Try adjusting your filters or search terms',
    clearFilters: 'Clear filters',
    allClear: 'All Clear!',
    noActiveAlerts: 'No active security alerts detected. Your API is running smoothly.',
    securityInsights: 'Security Insights',
    requestHealth: 'Request Health',
    excellent: 'Excellent',
    good: 'Good',
    needsAttention: 'Needs Attention',
    withinHealthyRange: 'within healthy range',
    higherThanNormal: 'higher than normal',
    threatLevel: 'Threat Level',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    activeAlertsMonitored: '{count} active alerts being monitored',
    botDetection: 'Bot Detection',
    monitoringAutomatedTraffic: 'Monitoring for automated traffic patterns',
    trafficAnalysis: 'Traffic Analysis',
    totalRequests: 'Total Requests',
    inLast24Hours: 'in last 24 hours',
    successRate: 'Success Rate',
    successful: 'successful',
    acrossAllRegions: 'across all regions',
    anomalyDetection: 'Anomaly Detection',
    anomalyDetectionRate: 'anomaly detection rate',
    monitoringActiveTitle: 'Security Monitoring Active',
    monitoringActiveBody: "Threat monitoring is running. We'll alert you quickly if suspicious activity is detected.",
    velocityAttacks: 'Velocity Attacks',
    detectsRapidRequests: 'Detects rapid request patterns',
    geoAnomalies: 'Geo Anomalies',
    identifiesImpossibleTravel: 'Identifies impossible travel',
    blocksAutomatedThreats: 'Blocks automated threats',
    upgradeRequired: 'Upgrade Required',
    upgradeBody: 'Anomaly detection is available on the Business plan.',
    viewPlans: 'View Plans',
    upgradeFeatureRealtime: 'Real-time velocity, geo, and bot detection',
    upgradeFeatureModel: 'Model-based risk scoring and insights',
    upgradeFeatureAlerts: 'Security alerts and actionable recommendations',
    details: 'Details',
    alertDetails: 'Alert Details',
    detectedThreats: 'Detected Threats',
    type: 'Type',
    score: 'Score',
    detectionInsights: 'Detection Insights',
    confidence: 'Confidence',
    resolution: 'Resolution',
    notesPlaceholder: 'Add notes about this alert (optional)...',
    markResolved: 'Mark Resolved',
    falsePositive: 'False Positive',
    investigating: 'Investigating',
    cancel: 'Cancel',
    justNow: 'just now',
    minutesAgo: '{count}m ago',
    hoursAgo: '{count}h ago',
    daysAgo: '{count}d ago',
    risk: 'Risk',
    noAlertsForFilters: 'No alerts for current filters.',
  },
  ownerStaff: {
    ownerTools: 'Owner Tools',
    notAuthorized: 'Not authorized',
    restrictedBody:
      'This area is restricted to platform owners. Owner tools control staff access and write directly to production permissions.',
    ownerAccessRequired: 'Owner access required',
    notInOwnerEmails: 'Your email {email} is not listed in PLATFORM_OWNER_EMAILS.',
    addAndReauth:
      'Add it there and re-authenticate with Google/GitHub SSO. In production, owner status is evaluated from environment config and SSO session.',
    manageStaffBody:
      'Manage staff (super admin) access. Changes apply immediately and are logged for audit. Use Super Admin tools for suspending or reactivating customer accounts.',
    grantRevokeTitle: 'Grant/Revoke Staff Access',
    email: 'Email',
    action: 'Action',
    reason: 'Reason',
    grantStaff: 'Grant Staff',
    revokeStaff: 'Revoke Staff',
    working: 'Working...',
    enterEmail: 'Enter an email to continue',
    provideReason: 'Please provide a reason (for audit logs)',
    currentStaff: 'Current Staff',
    noStaff: 'No staff accounts.',
    tableEmail: 'Email',
    tableId: 'ID',
    tableActions: 'Actions',
    revoke: 'Revoke',
    prev: 'Prev',
    next: 'Next',
    pageOf: 'Page {page} of {total}',
    recentActions: 'Recent Owner Actions',
    noRecentActions: 'No recent actions.',
    grantDialogTitle: 'Grant staff access?',
    revokeDialogTitle: 'Revoke staff access?',
    grantDialogDescription: 'This grants full platform access to {email}.',
    revokeDialogDescription: 'This removes platform-wide access from {email}.',
    confirmGrant: 'Grant',
    confirmRevoke: 'Revoke',
    grantSuccess: 'Granted staff for {email}',
    revokeSuccess: 'Revoked staff for {email}',
    failedUpdateStaff: 'Failed to update staff',
    failedRevoke: 'Failed to revoke',
    revokeReasonPlaceholder: 'Reason for revoking access',
    revokeReasonHint: 'Used for audit logs; be concise.',
    ownerManagedUpdateDefault: 'Owner-managed staff update',
    accessNoLongerRequiredDefault: 'Access no longer required',
  },
  ownerBlogManager: {
    title: 'Blog Manager',
    subtitle: 'Create and manage blog posts',
    accessRestricted: 'Access restricted',
    accessBody: 'Blog management requires owner or super admin access.',
    newPost: 'New Post',
    all: 'All',
    draft: 'Draft',
    published: 'Published',
    loadingPosts: 'Loading posts...',
    noPosts: 'No posts yet. Create your first blog post.',
    createFirstPost: 'Create your first post to start publishing updates.',
    createPost: 'Create post',
    tableTitle: 'Title',
    tableStatus: 'Status',
    tableAuthor: 'Author',
    tableDate: 'Date',
    tableActions: 'Actions',
    edit: 'Edit',
    view: 'View',
    delete: 'Delete',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {total}',
    deleteTitle: 'Delete post?',
    deleteBody: 'This action cannot be undone. The post will be permanently deleted.',
    cancel: 'Cancel',
    confirmDelete: 'Delete',
    toastPostDeleted: 'Post deleted',
    toastDeleteFailed: 'Failed to delete post',
  },
  ownerBlogEditor: {
    title: 'Blog Editor',
    accessRestricted: 'Access restricted',
    accessBody: 'Blog management requires owner or super admin access.',
    editPost: 'Edit Post',
    newPost: 'New Post',
    backToManager: 'Back to Blog Manager',
    saveDraft: 'Save Draft',
    publish: 'Publish',
    saving: 'Saving...',
    publishing: 'Publishing...',
    postTitlePlaceholder: 'Post title',
    status: 'Status',
    draft: 'Draft',
    published: 'Published',
    slug: 'URL Slug',
    slugPlaceholder: 'post-url-slug',
    excerpt: 'Excerpt',
    excerptPlaceholder: 'Brief summary for the blog listing...',
    featuredImageUrl: 'Featured Image URL',
    tags: 'Tags',
    tagsPlaceholder: 'EUDI, Product Update, Engineering',
    commaSeparated: 'Comma-separated',
    authorName: 'Author Name',
    authorNamePlaceholder: 'Author name',
    authorAvatarUrl: 'Author Avatar URL',
    remove: 'Remove',
    importMarkdown: 'Import Markdown',
    markdown: 'Markdown',
    markdownPlaceholder: 'Paste your markdown here...',
    preview: 'Preview',
    previewPlaceholder: 'Preview will appear here...',
    cancel: 'Cancel',
    import: 'Import',
    titleRequired: 'Title is required',
    contentRequired: 'Content is required',
    failedLoadPost: 'Failed to load post',
    recoveredDraft: 'Recovered unsaved draft',
    postPublished: 'Post published',
    draftSaved: 'Draft saved',
    draftCreated: 'Draft created',
    failedSavePost: 'Failed to save post',
    imageUploaded: 'Image uploaded',
    avatarUploaded: 'Avatar uploaded',
    uploadFailed: 'Upload failed',
    markdownImported: 'Markdown imported',
    startWritingPlaceholder: 'Start writing your blog post...',
  },
  superAdminDashboard: {
    loadingMetrics: 'Loading platform metrics...',
    platformOverview: 'Platform Overview',
    globalMetrics: 'Global metrics and performance analytics',
    noMetrics: 'No metrics available',
    quickActions: 'Quick Actions',
    platformSecurity: 'Platform Security',
    viewAllUsers: 'View All Users',
    demoRequests: 'Demo Requests',
    trialUsers: 'Trial Users',
    suspendedUsers: 'Suspended Users',
    userMetrics: 'User Metrics',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    activePaidUsers: 'Active Paid Users',
    mrr: 'MRR',
    monthlyRecurringRevenue: 'Monthly recurring revenue',
    ofTotal: '{value}% of total',
    ofActiveUsers: '{value}% of active users',
    planDistribution: 'Plan Distribution',
    trial: 'Trial',
    pro: 'Pro',
    business: 'Business',
    activeUsersWithTotal: 'Active users{totalSuffix}',
    verificationSessions: 'Verification Sessions',
    totalSessions: 'Total Sessions',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    growth: 'Growth',
    liveVerifications: 'Live Verifications',
    allTime: 'All-time',
    apiKeys: 'API Keys',
    totalKeys: 'Total Keys',
    activeKeys: 'Active Keys',
    liveKeys: 'Live Keys',
    productionEnvironment: 'Production environment',
  },
  superAdminSecurity: {
    superAdmin: 'Super Admin',
    platformSecurity: 'Platform Security',
    subtitle: 'Cross-merchant anomaly overview for staff users.',
    totalAlerts: 'Total Alerts',
    activeAlerts: 'Active Alerts',
    critical: 'Critical',
    topMerchantsLast7d: 'Top Merchants (last 7d)',
    filters: 'Filters',
    severity: 'Severity',
    status: 'Status',
    merchantIdOptional: 'Merchant ID (optional)',
    show: 'Show',
    any: 'Any',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    active: 'Active',
    investigating: 'Investigating',
    resolved: 'Resolved',
    falsePositive: 'False Positive',
    refresh: 'Refresh',
    securityAlerts: 'Security Alerts',
    noAlerts: 'No alerts for current filters.',
    time: 'Time',
    merchant: 'Merchant',
    type: 'Type',
    pageOf: 'Page {page} of {total}',
    prev: 'Prev',
    next: 'Next',
  },
  superAdminUsers: {
    superAdmin: 'Super Admin',
    allUsers: 'All Users',
    subtitle: 'Platform-wide user management and analytics',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    trialUsers: 'Trial Users',
    paidUsers: 'Paid Users',
    filters: 'Filters',
    exporting: 'Exporting...',
    exportCsv: 'Export CSV',
    hideFilters: 'Hide Filters',
    showFilters: 'Show Filters',
    search: 'Search',
    searchPlaceholder: 'Email, name, or company...',
    planTier: 'Plan Tier',
    allTiers: 'All Tiers',
    trial: 'Trial',
    pro: 'Pro',
    business: 'Business',
    status: 'Status',
    allStatuses: 'All Statuses',
    active: 'Active',
    suspended: 'Suspended',
    loadingUsers: 'Loading users...',
    noUsersFound: 'No users found',
    tableUser: 'User',
    tablePlan: 'Plan',
    tablePlanActive: 'Plan Active',
    tableUsage: 'Usage',
    tableResources: 'Resources',
    tableCreated: 'Created',
    tableActions: 'Actions',
    inactive: 'Inactive',
    admin: 'Admin',
    liveThisMonth: 'live this month',
    keys: 'keys',
    sessions: 'sessions',
    updated: 'Updated',
    viewSelf: 'View Self',
    viewDetails: 'View Details',
    usage: 'Usage',
    resources: 'Resources',
    created: 'Created',
    showingRange: 'Showing {start} to {end} of {total} users',
    previous: 'Previous',
    next: 'Next',
    exportStarted: 'CSV export started.',
    unableExport: 'Unable to export users.',
  },
  superAdminDemoRequests: {
    superAdmin: 'Super Admin',
    demoRequests: 'Demo Requests',
    subtitle: 'Track and manage demo booking requests from prospective customers',
    totalRequests: 'Total Requests',
    pending: 'Pending',
    contacted: 'Contacted',
    completed: 'Completed',
    allRequests: 'All Requests',
    pendingOnly: 'Pending Only',
    scheduled: 'Scheduled',
    onboarded: 'Onboarded',
    followUp: 'Follow Up',
    churned: 'Churned',
    showing: 'Showing {count} of {total} requests',
    loading: 'Loading demo requests...',
    noRequestsFound: 'No demo requests found',
    noRequestsAll:
      'No demo requests have been submitted yet. They will appear here once customers book demos from your landing page.',
    noRequestsWithStatus: 'No requests with "{status}" status. Try selecting a different filter.',
    contact: 'Contact',
    quickInfo: 'Quick Info',
    status: 'Status',
    submitted: 'Submitted',
    actions: 'Actions',
    hideDetails: 'Hide details',
    showDetails: 'Show details',
    noInfo: 'No info provided',
    email: 'Email',
    role: 'Role',
    useCase: 'Use Case',
    expectedVolume: 'Expected Volume',
    message: 'Message',
    viewFullDetails: 'View Full Details',
    pageOf: 'Page {page} of {total}',
    justNow: 'Just now',
    minutesAgo: '{count}m ago',
    hoursAgo: '{count}h ago',
    daysAgo: '{count}d ago',
    statusUpdated: 'Status updated successfully',
    statusUpdateFailed: 'Failed to update status',
  },
  notifications: {
    title: 'Notifications',
    subtitle: 'Stay informed about account activity, security alerts, and team updates.',
    searchPlaceholder: 'Search notifications...',
    allCategories: 'All categories',
    categorySecurity: 'Security',
    categoryBilling: 'Billing',
    categoryApi: 'API',
    categoryTeam: 'Team',
    allStatus: 'All',
    unreadOnly: 'Unread',
    readOnly: 'Read',
    clearFilters: 'Clear filters',
    loading: 'Loading notifications...',
    empty: 'No notifications yet. Activity will appear here as it happens.',
    noMatches: 'No notifications match your filters.',
    markRead: 'Mark read',
    markAllRead: 'Mark all as read',
    delete: 'Delete',
    viewAll: 'View all notifications',
    preferences: 'Preferences',
    preferencesTitle: 'Notification Preferences',
    preferencesSubtitle: 'Choose how you want to be notified for each category.',
    inApp: 'In-App',
    email: 'Email',
    savePreferences: 'Save preferences',
    savedPreferences: 'Notification preferences saved.',
    failedSavePreferences: 'Failed to save notification preferences.',
    category: 'Category',
    severity: 'Severity',
    timestamp: 'Time',
    pageSummary: 'Page {page} of {total}',
    previous: 'Previous',
    next: 'Next',
    bellNoNotifications: 'No new notifications',
    bellRecentTitle: 'Notifications',
    severityInfo: 'Info',
    severityWarning: 'Warning',
    severityError: 'Error',
    severityCritical: 'Critical',
    toastMarkedRead: 'Notification marked as read.',
    toastMarkedAllRead: 'All notifications marked as read.',
    toastDeleted: 'Notification deleted.',
    toastDeleteFailed: 'Failed to delete notification.',
    toastNewSingle: 'You have 1 new notification.',
    toastNewMultiple: 'You have {count} new notifications.',
  },
  superAdminUserDetail: {
    loadingUserDetails: 'Loading user details...',
    backToUsers: 'Back to Users',
    userNotFound: 'User not found',
    superAdmin: 'Super Admin',
    allUsers: 'All Users',
    editUser: 'Edit User',
    accountActions: 'Account Actions',
    accountActionsBody: 'Suspended accounts cannot sign in or create new verification sessions.',
    suspending: 'Suspending...',
    suspendAccount: 'Suspend account',
    reactivating: 'Reactivating...',
    reactivateAccount: 'Reactivate account',
    updateUser: 'Update User',
    planTier: 'Plan Tier',
    status: 'Status',
    free: 'Free',
    pro: 'Pro',
    business: 'Business',
    suspended: 'Suspended',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    accountInformation: 'Account Information',
    email: 'Email',
    name: 'Name',
    company: 'Company',
    emailVerified: 'Email Verified',
    verified: 'Verified',
    notVerified: 'Not Verified',
    authProvider: 'Auth Provider',
    created: 'Created',
    lastUpdated: 'Last Updated',
    planDetails: 'Plan Details',
    currentPlan: 'Current Plan',
    perMonth: 'month',
    testVerifications: 'Test Verifications',
    unlimited: 'Unlimited',
    liveVerifications: 'Live Verifications',
    maxTestKeys: 'Max Test Keys',
    maxLiveKeys: 'Max Live Keys',
    suspendTitle: 'Suspend this account?',
    suspendDescription:
      'Suspended users cannot sign in or create new verification sessions. Existing data remains intact.',
    suspend: 'Suspend',
    reactivateTitle: 'Reactivate this account?',
    reactivateDescription: 'Reactivating restores access immediately.',
    reactivate: 'Reactivate',
    totalSessions: 'Total Sessions',
    completed: 'Completed',
    failed: 'Failed',
    thisMonth: 'This Month',
    apiKeys: 'API Keys',
    totalKeys: 'Total Keys',
    activeKeys: 'Active Keys',
    publicId: 'Public ID',
    environment: 'Environment',
    lastUsed: 'Last Used',
    live: 'Live',
    test: 'Test',
    active: 'Active',
    revoked: 'Revoked',
    never: 'Never',
    showingRange: 'Showing {start} to {end} of {total} keys',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {total}',
  },
  onboarding: {
    title: 'Setup Checklist',
    progressLabel: '{completed} of {total} steps complete',
    dismiss: 'Dismiss',
    show: 'Show checklist',
    stepHasApiKey: 'Create an API key',
    stepHasVerification: 'Run your first verification',
    stepHasCompletedVerification: 'Complete a verification',
    stepHasWebhook: 'Set up a webhook',
    stepHasRedirectOrigin: 'Add a redirect origin',
    stepHasTeamMember: 'Invite a team member',
    stepHasTestKey: 'Create a test key',
    stepHasLiveKey: 'Create a live key',
    stepInstallSdk: 'Install the SDK',
    goToKeys: 'Go to Keys',
    goToPlayground: 'Go to Playground',
    goToWebhooks: 'Go to Webhooks',
    goToTeam: 'Go to Team',
    goToNpm: 'View on npm',
    allDone: 'All done! You\'re fully set up.',
  },
};

export default en;
